import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

declare let gtag: Function;

export interface CustomEventData {
  event_type: string;
  event_data?: any;
  page_url?: string;
  session_id?: string;
}

export interface FileData {
  name: string;
  type: string;
  size: number;
  operation?: string;
  bundle_id?: string;
  bundle_index?: number;
  user_type?: string;
  error_message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private sessionId: string = '';
  private currentSessionStartTime: Date = new Date();
  private pageViewCount: number = 0;
  private isGA4Loaded: boolean = false;
  private eventQueue: any[] = []; // Queue events until GA4 loads

  constructor(
    private dataService: DataService,
    private authService: AuthService
  ) {
    this.generateSessionId();
    this.startUserSession();
    
    // Make debug method available globally for debugging
    (window as any).debugAnalyticsUserId = () => this.debugUserIdInBackend();
    (window as any).debugGA4Status = () => this.debugGA4Status();
    console.log('🔧 Debug methods available:');
    console.log('  - window.debugAnalyticsUserId()');
    console.log('  - window.debugGA4Status()');
    
    // Make tracking methods available globally to avoid circular dependencies
    (window as any).trackLogin = (user: any, userType: string) => {
      const userData = { ...user, userType };
      this.trackUserLogin(userData);
    };
    (window as any).trackLogout = () => this.trackUserLogout();
  }

  // NEW: Debug GA4 status
  private debugGA4Status(): void {
    console.log('🔧 GA4 Debug Status:', {
      isGA4Loaded: this.isGA4Loaded,
      gtagExists: typeof gtag !== 'undefined',
      measurementId: environment.ga4MeasurementId,
      hostname: window.location.hostname,
      currentURL: window.location.href,
      eventQueueLength: this.eventQueue.length,
      sessionId: this.sessionId
    });
    
    // Check if gtag is available globally
    if (typeof (window as any).gtag === 'function') {
      console.log('✅ Global gtag function is available');
    } else {
      console.log('❌ Global gtag function is NOT available');
    }
    
    // Check dataLayer
    if ((window as any).dataLayer) {
      console.log('✅ dataLayer exists:', (window as any).dataLayer.length, 'items');
    } else {
      console.log('❌ dataLayer does NOT exist');
    }
  }

  // Initialize GA4
  initializeGA4(measurementId: string): void {
    console.log('🚀 Initializing GA4 with ID:', measurementId);
    
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    script1.onload = () => {
      console.log('📄 GA4 script loaded, setting up gtag...');
      
      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        
        // Check if we're in development mode
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' || 
                           window.location.hostname.includes('localhost');
        
        gtag('config', '${measurementId}', {
          cookie_flags: 'SameSite=None;Secure',
          send_page_view: false, // We'll handle page views manually
          debug_mode: isLocalhost, // Enable debug mode for localhost
          allow_google_signals: true,
          allow_ad_personalization_signals: true,
          // Allow events from any domain (important for localhost testing)
          cookie_domain: 'auto',
          // Disable automatic page view tracking
          page_title: document.title,
          page_location: window.location.href,
          // Ensure User ID is properly handled
          anonymize_ip: false,
          // Set custom parameters for anonymous users
          custom_map: {
            'custom_parameter_1': 'user_status'
          }
        });
        
        console.log('🔧 GA4 Configuration complete:', {
          measurementId: '${measurementId}',
          hostname: window.location.hostname,
          isLocalhost: isLocalhost,
          debugMode: isLocalhost,
          currentURL: window.location.href
        });
      `;
      document.head.appendChild(script2);
      
      // Wait a bit for gtag to be fully available
      setTimeout(() => {
        this.isGA4Loaded = true;
        console.log('✅ GA4 fully initialized and ready');
        
        // Process queued events
        this.processEventQueue();
        
        // Set user properties if user is logged in
        const user = this.getUserInfo();
        if (user) {
          this.setUserProperties(user);
        }
        
        // Send initial page view
        this.sendInitialPageView();
      }, 100);
    };

    script1.onerror = () => {
      console.error('❌ Failed to load GA4 script');
    };
  }

  // NEW: Send initial page view after GA4 is loaded
  private sendInitialPageView(): void {
    const currentUrl = window.location.pathname;
    const currentTitle = this.getPageTitle(currentUrl);
    
    console.log('🚀 === INITIAL PAGE VIEW DEBUG ===');
    console.log('📊 Sending initial page view:', { title: currentTitle, url: currentUrl });
    
    // Check if user is logged in
    const isLoggedIn = this.isLoggedIn();
    const user = this.getUserInfo();
    
    console.log('🔍 User Login Status:', {
      isLoggedIn: isLoggedIn,
      hasUser: !!user,
      userDetails: user
    });
    
    if (isLoggedIn && user) {
      // User is logged in - set their User ID
      const userId = this.getUserIdForGA4(user);
      console.log('✅ User is logged in, setting User ID:', userId);
      this.setUserId(userId);
    } else {
      // User is anonymous - explicitly clear User ID
      console.log('👤 User is anonymous, ensuring no User ID is set');
      this.clearUserId();
    }
    
    // Send page view with appropriate user context
    this.trackPageView(currentTitle, currentUrl);
    
    // Track anonymous user if not logged in
    if (!isLoggedIn) {
      console.log('🎯 === ANONYMOUS USER TRACKING ===');
      console.log('👤 About to track anonymous user app launch');
      console.log('🔧 GA4 Status before anonymous tracking:', {
        isGA4Loaded: this.isGA4Loaded,
        gtagExists: typeof gtag !== 'undefined'
      });
      
      this.trackAnonymousUserAction('app_launch', {
        landing_page: currentUrl,
        timestamp: new Date().toISOString(),
        user_status: 'anonymous'
      });
      
      console.log('✅ Anonymous user tracking call completed');
    } else {
      console.log('ℹ️ User is logged in, skipping anonymous tracking');
    }
    console.log('🏁 === INITIAL PAGE VIEW DEBUG END ===');
  }

  // Process queued events when GA4 loads
  private processEventQueue(): void {
    if (this.eventQueue.length > 0) {
      console.log(`🔄 Processing ${this.eventQueue.length} queued events...`);
      this.eventQueue.forEach(event => {
        if (event.eventName === 'config') {
          gtag('config', event.parameters.config_id, event.parameters);
        } else {
          gtag('event', event.eventName, event.parameters);
        }
      });
      this.eventQueue = [];
      console.log('✅ All queued events processed');
    }
  }

  // Generate unique session ID
  private generateSessionId(): void {
    this.sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Start user session tracking
  private startUserSession(): void {
    this.currentSessionStartTime = new Date();
    console.log('🎯 Session started:', this.sessionId);
    
    // Send session to backend
    this.sendSessionToBackend({
      session_id: this.sessionId,
      start_time: this.currentSessionStartTime.toISOString()
    });
  }

  // Get user info (adapted for openpyq structure)
  private getUserInfo(): any {
    const googleUser = localStorage.getItem('googleUser');
    if (googleUser) {
      try {
        return JSON.parse(googleUser);
      } catch {
        return null;
      }
    }
    return null;
  }

  // Check if user is logged in (adapted for openpyq structure)
  private isLoggedIn(): boolean {
    return !!localStorage.getItem('googleUser');
  }

  // Get page title based on URL
  private getPageTitle(url: string): string {
    const titleMap: { [key: string]: string } = {
      '/': 'Home',
      '/questions': 'Questions',
      '/search': 'Search',
      '/practice': 'Practice',
      '/yearwise': 'Yearwise Questions',
      '/topics': 'Topics',
      '/about': 'About',
      '/contact': 'Contact'
    };
    
    return titleMap[url] || document.title || 'OpenPYQ';
  }

  // Set user properties in GA4
  setUserProperties(user: any): void {
    if (!this.isGA4Loaded) return;

    const userId = this.getUserIdForGA4(user);
    
    gtag('set', {
      user_id: userId,
      custom_map: {
        dimension1: user.userType || 'user',
        dimension2: user.email
      }
    });

    gtag('set', 'user_properties', {
      user_type: user.userType || 'user',
      registration_date: user.created_at,
      last_login: user.last_login,
      is_logged_in: true
    });
  }

  // Get user ID for GA4 (must not be PII like email)
  private getUserIdForGA4(user: any): string {
    // Use non-PII user ID for GA4 compliance
    return user.id || user.sub || user.google_sub || `user_${Date.now()}`;
  }

  // Set User ID for logged-in users
  setUserId(userId: string): void {
    if (!userId) return;
    
    if (this.isGA4Loaded) {
      gtag('config', environment.ga4MeasurementId, {
        user_id: userId
      });
      console.log('✅ GA4 User ID set:', userId);
    } else {
      console.log('⏳ Queueing User ID (GA4 not ready):', userId);
      // Queue this for when GA4 loads
      this.eventQueue.push({ 
        eventName: 'config', 
        parameters: { 
          config_id: environment.ga4MeasurementId,
          user_id: userId 
        } 
      });
    }
  }

  // Clear User ID when user logs out
  clearUserId(): void {
    if (this.isGA4Loaded) {
      gtag('config', environment.ga4MeasurementId, {
        user_id: "NULL"  // Set to string "NULL" for easy filtering
      });
      console.log('✅ GA4 User ID set to NULL for anonymous user');
    }
  }

  // Track user login event
  trackUserLogin(user: any): void {
    const userId = this.getUserIdForGA4(user);
    
    console.log('🔑 Tracking user login:', { userId, userType: user.userType });
    
    // GA4 login event
    this.sendGtagEvent('login', {
      method: user.provider || 'google',
      user_id: userId,
      user_type: user.userType || 'user',
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'user_login',
      event_data: {
        user_id: userId,
        method: user.provider,
        user_type: user.userType
      },
      session_id: this.sessionId
    });
  }

  // Track user logout event
  trackUserLogout(): void {
    console.log('🔑 Tracking user logout');
    
    // GA4 logout event
    this.sendGtagEvent('logout', {
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'user_logout',
      event_data: {
        logout_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
  }

  // Send gtag event (with queuing if GA4 not loaded)
  private sendGtagEvent(eventName: string, parameters: any): void {
    if (this.isGA4Loaded) {
      gtag('event', eventName, parameters);
      console.log(`📊 GA4 Event Sent: ${eventName}`, parameters);
    } else {
      console.log(`⏳ Queueing GA4 Event: ${eventName}`, parameters);
      this.eventQueue.push({ eventName, parameters });
    }
  }

  // Track anonymous user actions
  trackAnonymousUserAction(action: string, data?: any): void {
    console.log('👤 Tracking anonymous user action:', action, data);
    
    // GA4 custom event for anonymous users
    this.sendGtagEvent('anonymous_user_action', {
      event_category: 'anonymous_engagement',
      event_label: action,
      value: 1,
      session_id: this.sessionId,
      user_status: 'anonymous',
      action_type: action,
      ...data
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'anonymous_user_action',
      event_data: {
        action: action,
        timestamp: new Date().toISOString(),
        user_status: 'anonymous',
        ...data
      },
      session_id: this.sessionId
    });
  }

  // Track custom events (for backend logging)
  trackCustomEvent(eventData: CustomEventData): void {
    console.log('📝 Custom Event:', eventData);
    
    // Send to backend analytics API
    this.sendToBackendAnalytics(eventData);
  }

  // NEW: Send analytics data to backend
  private sendToBackendAnalytics(eventData: CustomEventData): void {
    // Get user information for backend tracking
    const user = this.getUserInfo();
    const isLoggedIn = this.isLoggedIn();
    
    const backendEventData = {
      user_id: isLoggedIn && user ? this.getUserIdForGA4(user) : null,
      event_type: eventData.event_type,
      event_data: {
        ...eventData.event_data,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        user_status: isLoggedIn ? 'logged_in' : 'anonymous'
      },
      session_id: eventData.session_id || this.sessionId,
      page_url: eventData.page_url || window.location.href
    };

    // Send to backend (don't await to avoid blocking)
    fetch(`${environment.apiBaseUrl}/analytics/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(backendEventData)
    })
    .then(response => {
      if (response.ok) {
        console.log('✅ Backend analytics event sent successfully');
      } else {
        console.warn('⚠️ Backend analytics event failed:', response.status);
      }
    })
    .catch(error => {
      console.warn('⚠️ Backend analytics request failed:', error.message);
    });
  }

  // NEW: Send session data to backend
  private sendSessionToBackend(sessionData: any): void {
    const user = this.getUserInfo();
    const isLoggedIn = this.isLoggedIn();
    
    const backendSessionData = {
      user_id: isLoggedIn && user ? this.getUserIdForGA4(user) : null,
      session_id: sessionData.session_id || this.sessionId,
      start_time: sessionData.start_time || new Date().toISOString(),
      user_status: isLoggedIn ? 'logged_in' : 'anonymous'
    };

    // Send to backend (don't await to avoid blocking)
    fetch(`${environment.apiBaseUrl}/analytics/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(backendSessionData)
    })
    .then(response => {
      if (response.ok) {
        console.log('✅ Backend session created successfully');
      } else {
        console.warn('⚠️ Backend session creation failed:', response.status);
      }
    })
    .catch(error => {
      console.warn('⚠️ Backend session request failed:', error.message);
    });
  }

  // NEW: Send content analytics to backend
  private sendContentAnalyticsToBackend(contentType: string, contentId: string, contentName: string, action: 'view' | 'select'): void {
    const contentData = {
      content_type: contentType,
      content_id: contentId,
      content_name: contentName,
      action: action
    };

    // Send to backend (don't await to avoid blocking)
    fetch(`${environment.apiBaseUrl}/analytics/content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(contentData)
    })
    .then(response => {
      if (response.ok) {
        console.log('✅ Backend content analytics sent successfully');
      } else {
        console.warn('⚠️ Backend content analytics failed:', response.status);
      }
    })
    .catch(error => {
      console.warn('⚠️ Backend content analytics request failed:', error.message);
    });
  }

  // Track page views
  trackPageView(page_title: string, page_url: string): void {
    this.pageViewCount++;
    
    console.log('📄 Tracking page view:', { title: page_title, url: page_url, count: this.pageViewCount });
    
    // Determine user login status
    const isLoggedIn = this.isLoggedIn();
    const user = this.getUserInfo();
    const userStatus = isLoggedIn && user ? 'logged_in' : 'anonymous';
    
    // GA4 page view with user status
    this.sendGtagEvent('page_view', {
      page_title: page_title,
      page_location: window.location.href,
      page_path: page_url,
      session_id: this.sessionId,
      user_status: userStatus,
      // Add custom parameter to help with segmentation
      is_anonymous_user: !isLoggedIn
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'page_view',
      event_data: {
        page_title: page_title,
        page_url: page_url,
        page_view_count: this.pageViewCount,
        user_status: userStatus
      },
      page_url: page_url,
      session_id: this.sessionId
    });
  }

  // Track question view
  trackQuestionViewed(questionData: any): void {
    console.log('❓ Tracking question view:', questionData.id);
    
    // GA4 custom event
    this.sendGtagEvent('question_viewed', {
      event_category: 'engagement',
      event_label: questionData.topicId || 'unknown_topic',
      value: 1,
      session_id: this.sessionId,
      question_id: questionData.id,
      topic_id: questionData.topicId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'question_viewed',
      event_data: {
        question_id: questionData.id,
        topic_id: questionData.topicId,
        question_number: questionData.questionNumber,
        view_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
    
    // Send content analytics to backend
    this.sendContentAnalyticsToBackend(
      'question',
      questionData.id.toString(),
      `Question ${questionData.questionNumber}`,
      'view'
    );
  }

  // Track file download
  trackFileDownload(fileData: FileData): void {
    // Log debug info for localhost testing
    console.log('🔽 GA4 Download Event:', {
      fileData,
      hostname: window.location.hostname,
      ga4Loaded: this.isGA4Loaded,
      measurementId: environment.ga4MeasurementId
    });

    // GA4 custom event
    if (this.isGA4Loaded) {
      gtag('event', 'file_download', {
        event_category: 'engagement',
        event_label: fileData.type,
        value: Math.round(fileData.size / 1024), // Size in KB
        session_id: this.sessionId,
        // Add custom parameters for better tracking
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
      
      console.log('✅ GA4 file_download event sent:', {
        file_type: fileData.type,
        bundle_id: fileData.bundle_id,
        user_type: fileData.user_type,
        operation: fileData.operation
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'file_download',
      event_data: {
        file_name: fileData.name,
        file_type: fileData.type,
        file_size: fileData.size,
        download_time: new Date().toISOString(),
        operation: fileData.operation || 'download',
        bundle_id: fileData.bundle_id,
        bundle_index: fileData.bundle_index,
        user_type: fileData.user_type,
        error_message: fileData.error_message
      },
      session_id: this.sessionId
    });
  }

  // Track search
  trackSearch(searchTerm: string, resultsCount: number): void {
    // GA4 search event
    if (this.isGA4Loaded) {
      gtag('event', 'search', {
        search_term: searchTerm,
        results_count: resultsCount,
        session_id: this.sessionId
      });
    }

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'search',
      event_data: {
        search_term: searchTerm,
        results_count: resultsCount,
        search_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
  }

  // Track form interactions
  trackFormStart(formName: string): void {
    if (this.isGA4Loaded) {
      gtag('event', 'form_start', {
        form_name: formName,
        session_id: this.sessionId
      });
    }
  }

  trackFormSubmit(formName: string, success: boolean): void {
    if (this.isGA4Loaded) {
      gtag('event', 'form_submit', {
        form_name: formName,
        success: success,
        session_id: this.sessionId
      });
    }
  }

  // Track navigation
  trackNavigation(from: string, to: string): void {
    console.log('🧭 Tracking navigation:', { from, to });
    
    // GA4 custom event
    this.sendGtagEvent('navigation', {
      event_category: 'navigation',
      from_page: from,
      to_page: to,
      session_id: this.sessionId
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'navigation',
      event_data: {
        from_page: from,
        to_page: to,
        navigation_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
  }

  // Track topic selection
  trackTopicSelection(topicData: any): void {
    console.log('📚 Tracking topic selection:', topicData.topic_name);
    
    // GA4 custom event
    this.sendGtagEvent('topic_selected', {
      event_category: 'engagement',
      event_label: topicData.topic_name,
      value: 1,
      session_id: this.sessionId,
      topic_id: topicData.topic_id,
      topic_level: topicData.item_level
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'topic_selected',
      event_data: {
        topic_id: topicData.topic_id,
        topic_name: topicData.topic_name,
        topic_level: topicData.item_level,
        parent_topic_id: topicData.parent_topic_id,
        selection_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
    
    // Send content analytics to backend
    this.sendContentAnalyticsToBackend(
      'topic',
      topicData.topic_id.toString(),
      topicData.topic_name,
      'select'
    );
  }

  // Track exam selection (yearwise)
  trackExamSelection(examData: any): void {
    console.log('📝 Tracking exam selection:', examData.exam_name);
    
    // GA4 custom event
    this.sendGtagEvent('exam_selected', {
      event_category: 'engagement',
      event_label: examData.exam_name,
      value: 1,
      session_id: this.sessionId,
      exam_name: examData.exam_name,
      paper_name: examData.paper_name,
      year: examData.year
    });

    // Custom tracking
    this.trackCustomEvent({
      event_type: 'exam_selected',
      event_data: {
        exam_name: examData.exam_name,
        paper_name: examData.paper_name,
        year: examData.year,
        selection_time: new Date().toISOString()
      },
      session_id: this.sessionId
    });
    
    // Send content analytics to backend
    this.sendContentAnalyticsToBackend(
      'exam',
      `${examData.exam_name}_${examData.paper_name}_${examData.year}`,
      `${examData.exam_name} - ${examData.paper_name} (${examData.year})`,
      'select'
    );
  }

  // Debug method for backend user ID
  private debugUserIdInBackend(): void {
    console.log('🔍 Debug: Backend User ID Status');
    const user = this.getUserInfo();
    if (user) {
      const userId = this.getUserIdForGA4(user);
      console.log('User Info:', user);
      console.log('GA4 User ID:', userId);
      console.log('Is Logged In:', this.isLoggedIn());
    } else {
      console.log('No user found in localStorage');
    }
  }

  // Get connection status for validation
  getConnectionStatus(): { ga4: boolean, database: boolean, sessionId: string } {
    return {
      ga4: this.isGA4Loaded && typeof gtag === 'function',
      database: true, // Assuming database is available if service is loaded
      sessionId: this.sessionId
    };
  }
} 